import { Workflow, Sparkles, ShieldCheck } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

export function Login() {
  return (
    <main className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand pane */}
      <section className="lg:w-7/12 brand-gradient text-white flex flex-col px-8 sm:px-14 py-10 lg:py-16 relative overflow-hidden">
        <div className="absolute inset-0 brand-grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-accent-light/30 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />

        <header className="relative z-10">
          <Wordmark tone="inverse" size="lg" />
        </header>

        <div className="relative z-10 mt-auto pt-16 max-w-xl">
          <h1
            style={{ fontFamily: '"Fraunces", Georgia, serif', fontOpticalSizing: "auto" }}
            className="text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05]"
          >
            Keep more of the customers
            <br />
            <span className="italic text-white/85">you already won.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 leading-relaxed">
            RetentionIQ predicts who's about to churn, drafts the win-back, and
            queues the fix — across email, SMS and every channel. You approve
            every change.
          </p>

          <ul className="mt-10 space-y-4 text-sm text-white/85">
            <FeatureRow
              icon={<Sparkles size={16} strokeWidth={2} />}
              title="Predict, draft, queue"
              body="Churn risk, win-back copy and the right segment — drafted for you, across every channel."
            />
            <FeatureRow
              icon={<Workflow size={16} strokeWidth={2} />}
              title="Cross-channel, not just email"
              body="Revenue attributed across email, SMS, paid, direct & organic — the whole picture, not one slice."
            />
            <FeatureRow
              icon={<ShieldCheck size={16} strokeWidth={2} />}
              title="You stay in control"
              body="Nothing sends without your sign-off. Dry-run-gated, revertible, full audit log."
            />
          </ul>
        </div>

        <footer className="relative z-10 mt-12 lg:mt-16 text-xs text-white/55">
          Part of the IQ Suite — sibling to AdsIQ &amp; StoresIQ.
        </footer>
      </section>

      {/* Sign-in pane */}
      <section className="lg:w-5/12 bg-bg flex items-center justify-center px-6 py-12 lg:py-0">
        <Card className="p-8 max-w-sm w-full">
          <h2 className="text-xl font-semibold text-text-primary">Sign in</h2>
          <p className="text-sm text-text-muted mt-1">
            Continue to your brand dashboards.
          </p>

          <Button
            variant="ghost"
            className="mt-6 w-full gap-2.5 py-2.5 h-auto text-text-primary font-medium"
            onClick={() => (window.location.href = "/auth/google/login")}
          >
            <GoogleMark />
            Continue with Google
          </Button>

          <Button
            variant="ghost"
            className="mt-3 w-full gap-2.5 py-2.5 h-auto text-text-primary font-medium"
            onClick={() => (window.location.href = "/auth/microsoft/login")}
          >
            <MicrosoftMark />
            Continue with Microsoft
          </Button>

          <div className="mt-6 pt-6 border-t border-border-lighter text-xs text-text-dim leading-relaxed">
            By continuing you agree to our terms of service. We never send on
            your behalf without your approval.
          </div>
        </Card>
      </section>
    </main>
  );
}

function FeatureRow({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/10 ring-1 ring-white/15 shrink-0">
        {icon}
      </span>
      <div>
        <div className="font-medium text-white">{title}</div>
        <div className="text-white/70">{body}</div>
      </div>
    </li>
  );
}

function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.25-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
